export function captureVideoFrame(
  video: HTMLVideoElement,
  type: "image/png" | "image/jpeg" = "image/png",
  quality = 0.9
): string | null {
  try {
    if (!video.videoWidth || !video.videoHeight) return null;

    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    const ctx = canvas.getContext("2d");
    if (!ctx) return null;

    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    return canvas.toDataURL(type, quality);
  } catch (e) {
    console.error("captureVideoFrame error", e);
    return null;
  }
}

export async function captureVideoFrameBytes(
  video: HTMLVideoElement,
  type: "image/png" | "image/jpeg" = "image/png",
  quality = 0.1,
  maxSize = Infinity
): Promise<Uint8Array | null> {
  try {
    if (!video.videoWidth || !video.videoHeight) return null;

    const scale = Math.min(1, maxSize / Math.max(video.videoWidth, video.videoHeight));
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(video.videoWidth * scale);
    canvas.height = Math.round(video.videoHeight * scale);

    const ctx = canvas.getContext("2d");
    if (!ctx) return null;

    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Prefer toBlob() (more memory-friendly than toDataURL)
    const blob: Blob | null = await new Promise((resolve) =>
      canvas.toBlob(resolve, type, type === "image/jpeg" ? quality : undefined)
    );

    if (!blob) return null;

    const buf = await blob.arrayBuffer();
    return new Uint8Array(buf);
  } catch (e) {
    console.error("captureVideoFrameBytes error", e);
    return null;
  }
}

type FrameBytes = Uint8Array | null;

export async function captureVideoFrameBytesPngScaled(
  video: HTMLVideoElement,
  opts?: {
    maxWidth?: number;
    maxHeight?: number;
    opaque?: boolean;
  }
): Promise<FrameBytes> {
  const { maxWidth = 1280, maxHeight = 720, opaque = true } = opts ?? {};

  const vw = video.videoWidth;
  const vh = video.videoHeight;
  if (!vw || !vh) return null;

  const scale = Math.min(maxWidth / vw, maxHeight / vh, 1);
  const w = Math.max(1, Math.round(vw * scale));
  const h = Math.max(1, Math.round(vh * scale));

  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;

  const ctx = canvas.getContext("2d", { alpha: !opaque });
  if (!ctx) return null;

  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";

  if (opaque) {
    ctx.fillStyle = "#000";
    ctx.fillRect(0, 0, w, h);
  }

  ctx.drawImage(video, 0, 0, w, h);

  const blob: Blob | null = await new Promise((resolve) =>
    canvas.toBlob(resolve, "image/png")
  );
  if (!blob) return null;

  const buf = await blob.arrayBuffer();
  return new Uint8Array(buf);
}
